import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  requestAndSubscribe,
  unsubscribeAndUnregister,
  getSavedSubscriptionEndpoint,
} from '../../services/pushSubscriptionService';

/**
 * Hook para estado e ações de Web Push.
 * @returns {{
 *   isSupported: boolean,
 *   permission: NotificationPermission | null,
 *   isSubscribed: boolean,
 *   subscribe: () => Promise<boolean>,
 *   unsubscribe: () => Promise<void>,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export default function usePushSubscription() {
  const [permission, setPermission] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isSupported = isPushSupported();

  const updateState = useCallback(async () => {
    if (!isSupported) {
      setPermission(null);
      setIsSubscribed(false);
      return;
    }
    setPermission(Notification.permission);
    const hasEndpoint = !!getSavedSubscriptionEndpoint();
    if (Notification.permission === 'granted') {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!(sub || hasEndpoint));
      } catch (_) {
        setIsSubscribed(hasEndpoint);
      }
    } else {
      setIsSubscribed(false);
    }
  }, [isSupported]);

  useEffect(() => {
    updateState();
  }, [updateState]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    setLoading(true);
    setError(null);
    try {
      const subscription = await requestAndSubscribe();
      setPermission(Notification.permission);
      setIsSubscribed(!!subscription);
      return !!subscription;
    } catch (err) {
      const message = err?.message || 'Não foi possível ativar as notificações.';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await unsubscribeAndUnregister();
      setPermission(Notification.permission);
      setIsSubscribed(false);
    } catch (err) {
      setError(err?.message || 'Erro ao desativar.');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed,
    subscribe,
    unsubscribe,
    loading,
    error,
    updateState,
  };
}
